import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import LocationCard from './LocationCard';
import LocationForm from './LocationForm';
import LoadingSpinner from './LoadingSpinner';
import './LocationList.css';

const LocationList = ({ 
  siteId, 
  siteName, 
  showCreateButton = true, 
  onLocationSelect = null,
  compact = false 
}) => {
  const { 
    loading, 
    error, 
    loadLocationsBySite,
    getLocationsBySite,
    deleteLocation
  } = useSubdomain();

  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  // Obtener ubicaciones del site específico
  const siteLocations = getLocationsBySite(siteId);

  // Cargar locations cuando cambie el siteId
  useEffect(() => {
    if (siteId && !locationsLoaded) {
      loadLocationsBySite(siteId);
      setLocationsLoaded(true);
    }
  }, [siteId, loadLocationsBySite, locationsLoaded]);

  const handleCreateLocation = () => {
    setEditingLocation(null);
    setShowLocationForm(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setShowLocationForm(true);
  };

  const handleLocationFormSuccess = () => {
    setShowLocationForm(false);
    setEditingLocation(null);
    if (siteId) {
      loadLocationsBySite(siteId);
    }
  };

  const handleLocationFormCancel = () => {
    setShowLocationForm(false);
    setEditingLocation(null);
  };

  const handleDeleteLocation = async (location) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la ubicación "${location.name}"? Esta acción no se puede deshacer.`)) {
      try {
        await deleteLocation(location.id, siteId);
        if (siteId) {
          loadLocationsBySite(siteId);
        }
      } catch (error) {
        console.error('Error al eliminar la ubicación:', error);
        alert('Error al eliminar la ubicación. Por favor, intenta nuevamente.');
      }
    }
  };

  const handleLocationClick = (location) => {
    if (onLocationSelect) {
      onLocationSelect(location);
    }
  };

  if (loading && !locationsLoaded) {
    return <LoadingSpinner message="Cargando ubicaciones..." />;
  }

  if (error) {
    return (
      <div className="location-list-error">
        <div className="error-content">
          <h3>❌ Error al cargar ubicaciones</h3>
          <p>{error}</p>
          <button 
            onClick={() => {
              setLocationsLoaded(false);
              loadLocationsBySite(siteId);
            }}
            className="btn btn-primary"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Mostrar formulario de creación/edición
  if (showLocationForm) {
    return (
      <div className="location-form-container">
        <LocationForm
          onSuccess={handleLocationFormSuccess}
          onCancel={handleLocationFormCancel}
          editingLocation={editingLocation}
          siteId={siteId}
        />
      </div>
    );
  }

  return (
    <div className={`location-list ${compact ? 'compact' : ''}`}>
      <div className="location-list-header">
        <h2>
          <span className="icon">📍</span>
          Ubicaciones {siteName && `- ${siteName}`}
        </h2>
        {showCreateButton && (
          <button 
            onClick={handleCreateLocation}
            className="btn btn-success"
          >
            ➕ Nueva Ubicación
          </button>
        )}
      </div>

      {siteLocations.length === 0 ? (
        <div className="no-locations">
          <div className="no-locations-icon">📍</div>
          <h3>No hay ubicaciones configuradas</h3>
          <p>Comienza creando tu primera ubicación para este centro de entrenamiento.</p>
          {showCreateButton && (
            <button 
              onClick={handleCreateLocation}
              className="btn btn-primary btn-lg"
            >
              Crear Primera Ubicación
            </button>
          )}
        </div>
      ) : (
        <div className="locations-grid">
          {siteLocations.map(location => (
            <LocationCard
              key={location.id}
              location={location}
              onEdit={handleEditLocation}
              onDelete={handleDeleteLocation}
              onClick={onLocationSelect ? () => handleLocationClick(location) : null}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationList;
